/**
 * CORE-1957 — native telemetry queues are flushed when the app leaves the
 * foreground.
 *
 * Both queues (`analytics`, `liveTelemetry`) live in memory on native and go out
 * on a 5 s interval. The web flushes them on `pagehide` / `visibilitychange`;
 * native had nothing, so a person who left the room and swiped the app away
 * within those seconds left no trace at all (QA 28.10.2026, Note8: «при выходе и
 * закрытии приложения событий нет»). Going to the background is the last moment
 * JS reliably runs before the OS may kill the process, so that is when we send.
 *
 * Web is untouched: it has its own unload paths and `AppState` there is a
 * visibility shim we do not want a second flush from.
 */
import { AppState, Platform } from 'react-native';

/** States in which the app may be suspended or killed without another chance to send. */
export function isLeavingForeground(next: string | null | undefined): boolean {
  return next === 'background' || next === 'inactive';
}

/** Subscribe `flush` to the app going to the background (native only). Never throws. */
export function installNativeBackgroundFlush(flush: () => void): void {
  if (Platform.OS === 'web') return;
  try {
    if (typeof AppState?.addEventListener !== 'function') return;
    // Lives as long as the queue it serves (a module singleton): never removed.
    AppState.addEventListener('change', (next: string) => {
      if (!isLeavingForeground(next)) return;
      try { flush(); } catch { /* telemetry never breaks the app */ }
    });
  } catch {
    /* no AppState — the interval flush stays the only path, as before */
  }
}
